import { useMemo, type ReactNode } from 'react';
// Extensionless: this file is bundled by Vite (admin) and Metro (mobile).
import { useBusinessDay } from '../hooks/useBusinessDay';
import { useElapsed } from '../hooks/useElapsed';
import { useTranslation } from '../i18n/index';

/**
 * One thing that happened to a job, as the journey endpoint reports it.
 * Deliberately the same shape the API returns, so nothing has to be mapped on
 * the way in and a new field needs no change here.
 */
export interface JourneyStep {
  readonly occurredAt: string;
  readonly stage: string;
  readonly action: string;
  readonly actorName?: string | null;
  readonly actorUserId?: string | null;
  readonly remark?: string | null;
  readonly detail?: string | null;
  readonly source?: string;
}

/**
 * JobJourney — a job's life drawn as a line, not tabulated.
 *
 * A table of timestamps answers "what happened" but not "how far has this
 * got", which is the question everyone actually opens the screen with. This
 * draws the stages the job has passed through as a spine, with each thing that
 * happened hanging off it in order, so a glance tells the story and the detail
 * is there for anyone who wants it.
 *
 * **Nothing about the shape of a job is written here.** The stages are
 * whatever the journey reported, in the order they occurred — a tenant that
 * adds an approval level, or a module that starts recording a new kind of
 * event, appears without a line of code changing. That matters: the approval
 * chain is configured per tenant in the workflow designer, so a drawing with
 * the stages baked in would be wrong for somebody from the day it shipped.
 */
export function JobJourney({
  steps,
  stageLabels = {},
  emptyMessage = 'Nothing has happened to this job yet.',
  ariaLabel = 'job-journey',
}: {
  readonly steps: readonly JourneyStep[];
  /** Pretty names for stage codes. A stage with no label shows its code. */
  readonly stageLabels?: Readonly<Record<string, string>>;
  readonly emptyMessage?: string;
  readonly ariaLabel?: string;
}): ReactNode {
  // Instants are written in the tenant's date format on the reader's clock.
  const { at } = useBusinessDay();
  const { exactly } = useElapsed();
  const { t } = useTranslation();
  /** The stages this job actually passed through, in the order it met them. */
  const stages = useMemo(() => {
    const seen: string[] = [];
    for (const s of steps) if (!seen.includes(s.stage)) seen.push(s.stage);
    return seen;
  }, [steps]);

  const grouped = useMemo(
    () =>
      stages.map((stage) => ({
        stage,
        label: stageLabels[stage] ?? stage,
        steps: steps.filter((s) => s.stage === stage),
      })),
    [stages, steps, stageLabels],
  );

  if (steps.length === 0) {
    return (
      <p aria-label={`${ariaLabel}-empty`} className="journey-empty">
        {emptyMessage}
      </p>
    );
  }

  const last = steps[steps.length - 1];
  const currentIndex = stages.indexOf(last.stage);
  /** Whole minutes since the step before it in the same stage; none under one. */
  const gapBefore = (group: readonly JourneyStep[], i: number): number | null => {
    if (i <= 0) return null;
    const ms = Date.parse(group[i].occurredAt) - Date.parse(group[i - 1].occurredAt);
    const minutes = Number.isFinite(ms) ? Math.floor(ms / 60000) : 0;
    return minutes >= 1 ? minutes : null;
  };

  return (
    <section aria-label={ariaLabel} className="journey">
      {/* The spine: every stage the job has reached, in order. Stages behind it
          are done, the one it is standing on now is marked. */}
      <ol aria-label={`${ariaLabel}-stages`} className="journey-spine">
        {grouped.map((g, i) => {
          const current = i === currentIndex;
          // Every stage drawn here comes from things that already happened, so
          // each one but the stage the job is standing on now is behind it.
          const done = !current;
          return (
            <li
              key={g.stage}
              data-testid={`journey-stage-${g.stage}`}
              data-current={current ? 'true' : 'false'}
              className={`journey-stage${current ? ' is-current' : ''}${done ? ' is-done' : ''}`}
            >
              <span className="journey-stage-dot" aria-hidden="true">
                {done ? '\u2713' : i + 1}
              </span>
              <span className="journey-stage-name">{g.label}</span>
              <span className="journey-stage-count">{g.steps.length}</span>
              {current ? (
                <span className="journey-stage-now">{t('journey.now')}</span>
              ) : null}
            </li>
          );
        })}
      </ol>

      {/* Everything that happened, hanging off the spine in the order it
          happened. Grouped by stage so the eye can follow the handover. */}
      <div className="journey-body">
        {grouped.map((g, gi) => (
          <section
            key={g.stage}
            aria-label={`${ariaLabel}-${g.stage}`}
            className={`journey-group${gi === currentIndex ? ' is-current' : ''}`}
          >
            <header className="journey-group-head">
              <span className="journey-group-badge" aria-hidden="true">{gi + 1}</span>
              <h3 className="journey-group-title">{g.label}</h3>
              <span className="journey-group-span">
                {at(g.steps[0].occurredAt)}
                {g.steps.length > 1 ? ` \u2192 ${at(g.steps[g.steps.length - 1].occurredAt)}` : ''}
              </span>
            </header>
            <ol className="journey-events">
              {g.steps.map((s, i) => {
                const gap = gapBefore(g.steps, i);
                const latest = s === last;
                return (
                  <li
                    key={`${s.occurredAt}-${s.stage}-${i}`}
                    className={`journey-event${latest ? ' is-latest' : ''}`}
                    data-testid={`journey-event-${g.stage}-${i}`}
                  >
                    {gap !== null ? (
                      <span className="journey-gap">
                        {t('journey.later', { gap: exactly(gap) })}
                      </span>
                    ) : null}
                    <div className="journey-card">
                      <div className="journey-card-top">
                        <span className="journey-what">{s.action}</span>
                        <time className="journey-when" dateTime={s.occurredAt}>
                          {at(s.occurredAt)}
                        </time>
                      </div>
                      {s.actorName ? (
                        <span className="journey-who">
                          <span className="journey-avatar" aria-hidden="true">
                            {initials(s.actorName)}
                          </span>
                          {s.actorName}
                        </span>
                      ) : null}
                      {s.detail ? (
                        <span className="journey-detail">{s.detail}</span>
                      ) : null}
                      {s.remark ? (
                        <q className="journey-remark">{s.remark}</q>
                      ) : null}
                      {latest ? (
                        <span className="journey-latest">{t('journey.latest')}</span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </section>
  );
}

/** Two letters for the person's badge: first and last word of the name. */
function initials(name: string): string {
  const words = name.replace(/\(.*?\)/g, '').trim().split(/[\s_]+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0][0] ?? '';
  const second = words.length > 1 ? words[words.length - 1][0] : words[0][1] ?? '';
  return (first + second).toUpperCase();
}

